import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useActivateWeekFreeze, useFlags, useFreezeAuto, usePassFreezes } from '../api/hooks';
import { Loader } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { backButton, haptics } from '../telegram';
import { SnowIcon } from './PassScreen';
import { RARITY_LABEL } from '../components/cosmetics/cosmeticsMap';

const FREEZE_KIND_NAME: Record<string, string> = {
  game1: 'На 1 игру',
  game3: 'На 3 игры',
  week: 'На неделю',
  eternal: 'Вечная',
};

const WEEKDAY_ACC = [
  'в воскресенье',
  'в понедельник',
  'во вторник',
  'в среду',
  'в четверг',
  'в пятницу',
  'в субботу',
];

/** 5.1 — экран «Заморозки» (вынесен с 6.1 по макету; вход из профиля и
 *  вкладки «Отбор»): активная карточка, инвентарь зарядов, авто-трата,
 *  история, закреплённая кнопка недели. */
export function FreezesScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const freezes = usePassFreezes(enabled);
  const activateWeek = useActivateWeekFreeze();
  const freezeAuto = useFreezeAuto();
  const navigate = useNavigate();

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;
  if (!flags.data || freezes.isLoading) return <Loader />;
  const f = freezes.data;
  if (!f || f.status !== 'active' || !f.freezesEnabled) {
    return (
      <div className="bp-screen">
        <Head onBack={() => navigate(-1)} />
        <div className="q-empty">Заморозки сейчас недоступны.</div>
      </div>
    );
  }
  const auto = f.auto ?? true;
  const b = f.balances ?? { game1: 0, game3: 0, week: 0 };
  const weekLeft = f.weekActiveUntil
    ? Math.max(0, new Date(f.weekActiveUntil).getTime() - Date.now())
    : 0;
  const weekOn = weekLeft > 0;
  const weekD = Math.floor(weekLeft / 86400_000);
  const weekH = Math.floor((weekLeft % 86400_000) / 3600_000);

  return (
    <div className="bp-screen bpf-screen">
      <Head onBack={() => navigate(-1)} />
      <div className="bpf-title">Заморозки</div>
      <div className="bpf-sub">
        Защищают баллы отбора при пропуске игр. На результаты игр не влияют.
      </div>

      {/* Активная заморозка (карточка #0A1530, макет 5.1) */}
      {f.eternal && (
        <div className="bpf-hero">
          <span className="tag mono">НА ВЕСЬ КЛУБ</span>
          <span className="main">
            <b>Вечная заморозка</b>
            <span>баллы не убывают до конца отбора</span>
          </span>
          <span className="big mono">∞</span>
        </div>
      )}
      {weekOn && (
        <div className="bpf-hero">
          <span className="tag mono">ВРУЧНУЮ</span>
          <span className="main">
            <b>Активна · неделя</b>
            <span>пропуски этой недели не спишут баллы</span>
          </span>
          <span className="big mono">
            {weekD}д<i>{String(weekH).padStart(2, '0')}ч</i>
          </span>
        </div>
      )}

      <div className="bpf-grid">
        {(['game1', 'game3', 'week'] as const).map((k) => (
          <div key={k} className={`bpf-card${b[k] === 0 ? ' bpf-card--zero' : ''}`}>
            <span className="ic">
              <SnowIcon size={18} />
              <b className="pill mono">{k === 'game1' ? '1' : k === 'game3' ? '3' : '7д'}</b>
            </span>
            <span className="nm">{FREEZE_KIND_NAME[k]}</span>
            <span className="how">{k === 'week' ? 'вручную' : k === 'game3' ? 'авто, по одной' : 'авто'}</span>
            <span className="cnt mono">×{b[k]}</span>
          </div>
        ))}
      </div>

      <div className="bpf-settings">
        <label className="bpf-auto">
          <span className="txt">
            <b>Тратить автоматически</b>
            <span>
              {auto
                ? 'На пропуск тратится сначала «1 игра», потом «3 игры»'
                : 'Выключено: заряды не тратятся, баллы списываются'}
            </span>
          </span>
          <span className={`sw${auto ? ' on' : ''}`}>
            <input
              type="checkbox"
              checked={auto}
              onChange={(e) => {
                haptics.tap();
                void freezeAuto.mutateAsync(e.target.checked).catch(() => haptics.error());
              }}
            />
          </span>
        </label>

        {(f.history ?? []).length > 0 && (
          <div className="bpf-history">
            {(f.history ?? []).slice(0, 8).map((h, i) => (
              <div key={i} className="bpf-hrow">
                <span className={`sn${h.type === 'saved' ? ' saved' : ''}`}>
                  <SnowIcon size={14} />
                </span>
                <span className="tx">
                  {h.type === 'saved'
                    ? `Заморозка спасла баллы за пропуск${h.gameAt ? ` ${WEEKDAY_ACC[new Date(h.gameAt).getDay()]}` : ''}`
                    : h.kind === 'eternal'
                      ? 'Выпала вечная заморозка'
                      : `Выпала из ${h.deckRarity !== null ? RARITY_LABEL[h.deckRarity].toLowerCase() + ' колоды' : 'колоды'} · ${FREEZE_KIND_NAME[h.kind]?.toLowerCase() ?? h.kind}`}
                </span>
                <span className="dt mono">{new Date(h.at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Кнопка недели — закреплена внизу (макет) */}
      {(b.week > 0 || weekOn) && !f.eternal && (
        <div className="bp-bottom">
          {weekOn ? (
            <>
              <button className="btn-secondary bp-btn-secondary bpf-weekon" disabled>
                <SnowIcon size={14} /> Неделя включена
              </button>
              <div className="bp-bottom-note mono">Отменить можно до первого пропуска</div>
            </>
          ) : (
            <>
              <button
                className="btn bp-claim-all"
                disabled={activateWeek.isPending}
                onClick={() => {
                  haptics.tap();
                  void activateWeek
                    .mutateAsync()
                    .then(() => haptics.success())
                    .catch(() => haptics.error());
                }}
              >
                <SnowIcon size={14} /> Включить неделю
              </button>
              <div className="bp-bottom-note mono">Покроет 7 дней · можно заранее</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Head({ onBack }: { onBack: () => void }) {
  return (
    <div className="subhead">
      <button className="iconbtn" onClick={onBack} aria-label="Назад">
        <BackIcon />
      </button>
      <div className="title">Отбор</div>
    </div>
  );
}
